import { apiService } from '@/services/apiService';
import { accountIdFromToken } from '@/utils/accountId';
import { GUIDED_ADD_INTENT_KEY } from '@/utils/playerSessionStorage';

/**
 * Guided Add money: what the player chose before paying (spec §5, plan Task 4). It survives leaving
 * the page during a payment, so Money and the guided page can offer "Finish adding money" once My
 * money covers it. Nothing moves to a game until the player confirms there.
 * Kept in sessionStorage for two hours and only for the player who saved it (`userId`): another
 * player on a shared kiosk or family phone never gets it, and sign-out / sign-in drop it
 * (apiService → clearPlayerSessionStorage). Storage errors (private mode, quota) are ignored.
 */
export interface GuidedAddIntent {
  targets: Array<{ gameProviderId: string; amount: number }>;
  walletOnlyAmount?: number;
  createdAt: number;
  /** The player who saved it (stamped by saveGuidedAddIntent). */
  userId?: string;
}

const STORAGE_KEY = GUIDED_ADD_INTENT_KEY;
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** The signed-in player, or null when signed out. */
function currentPlayer(): string | null {
  try {
    return accountIdFromToken(apiService.getAccessToken());
  } catch {
    return null;
  }
}

function isTarget(value: unknown): value is { gameProviderId: string; amount: number } {
  const t = value as { gameProviderId?: unknown; amount?: unknown } | null;
  return !!t && typeof t.gameProviderId === 'string' && typeof t.amount === 'number' && Number.isFinite(t.amount);
}

function parseIntent(raw: string): GuidedAddIntent | null {
  try {
    const value = JSON.parse(raw) as Partial<GuidedAddIntent> | null;
    if (!value || typeof value.createdAt !== 'number' || !Number.isFinite(value.createdAt)) return null;
    if (!Array.isArray(value.targets) || !value.targets.every(isTarget)) return null;
    const walletOnly = value.walletOnlyAmount;
    if (walletOnly !== undefined && (typeof walletOnly !== 'number' || !Number.isFinite(walletOnly))) return null;
    if (value.userId !== undefined && typeof value.userId !== 'string') return null;
    return {
      targets: value.targets.map(({ gameProviderId, amount }) => ({ gameProviderId, amount })),
      ...(walletOnly !== undefined ? { walletOnlyAmount: walletOnly } : {}),
      createdAt: value.createdAt,
      ...(value.userId !== undefined ? { userId: value.userId } : {}),
    };
  } catch {
    return null;
  }
}

/** Saves the intent for the signed-in player (nothing is saved when signed out). */
export function saveGuidedAddIntent(i: GuidedAddIntent): void {
  const userId = currentPlayer();
  if (!userId) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...i, userId }));
  } catch {
    // Storage unavailable: the flow still works, it just cannot be resumed later.
  }
}

/**
 * The signed-in player's stored intent, or null when there is none, it is unreadable, older than two
 * hours or not theirs (one that is not theirs is dropped).
 */
export function readGuidedAddIntent(now: number = Date.now()): GuidedAddIntent | null {
  const player = currentPlayer();
  if (!player) return null;
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const intent = parseIntent(raw);
  if (!intent || intent.userId !== player || now - intent.createdAt > MAX_AGE_MS) {
    clearGuidedAddIntent();
    return null;
  }
  return intent;
}

export function clearGuidedAddIntent(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
}

/** What the intent sends to games (a wallet-only top-up sends nothing on). */
export function guidedAddIntentTotal(intent: GuidedAddIntent): number {
  return intent.targets.reduce((sum, target) => sum + target.amount, 0);
}

/**
 * "Finish adding money" is offered (guided page, pending floater) when games are waiting for money
 * and My money now covers them. A wallet-only top-up is finished by its payment.
 */
export function canFinishGuidedAdd(intent: GuidedAddIntent | null, available: number): boolean {
  return !!intent && intent.targets.length > 0 && available >= guidedAddIntentTotal(intent);
}
